import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  orderBy, 
  getDocs,
  getDoc,
  Timestamp,
  where,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { Customer, CreateCustomerData, CustomerWithFieldValues } from '../../types/customer';
import { customerHistoryService } from './customerHistoryService';

const COLLECTION = 'customers';

export const customerService = {
  async create(data: CreateCustomerData, userId: string) {
    console.log("========== CUSTOMER SERVICE CREATE ==========");
    console.log("1. Received data:", data);
    console.log("2. Has contactPersons?", 'contactPersons' in data);
    console.log("3. ContactPersons value:", data.contactPersons);
    
    const customerData = {
      ...data,
      status: 'active' as const,
      isActive: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: userId
    };

    console.log("4. Final customerData to save:", customerData);
    console.log("5. ContactPersons in final data:", customerData.contactPersons);
    console.log("========== SAVING TO FIRESTORE ==========");

    const docRef = await addDoc(collection(db, COLLECTION), customerData);
    const createdId = docRef.id;
    
    console.log("6. Saved with ID:", createdId);
    console.log("========== CUSTOMER SERVICE CREATE END ==========");
    
    // Record history entry for customer creation
    await customerHistoryService.recordChange(
      createdId,
      null,
      { ...customerData, id: createdId } as Partial<CustomerWithFieldValues>,
      userId,
      'create'
    );
    
    return createdId;
  },

  async update(id: string, data: Partial<Customer>, userId?: string) {
    const docRef = doc(db, COLLECTION, id);
    
    // Get current data to track changes
    const currentDocSnapshot = await getDoc(docRef);
    if (!currentDocSnapshot.exists()) {
      throw new Error(`Customer with ID ${id} not found`);
    }
    
    const currentData = { 
      id,
      ...currentDocSnapshot.data()
    } as CustomerWithFieldValues;
    
    const updateData = {
      ...data,
      ...(data.status && { isActive: data.status === 'active' }),
      organizations: data.organizations || [],
      updatedAt: serverTimestamp()
    };
    
    // Update the customer record
    await updateDoc(docRef, updateData);
    
    // Record history entry for update if we have a userId
    if (userId) {
      await customerHistoryService.recordChange(
        id,
        currentData,
        { ...currentData, ...updateData } as Partial<CustomerWithFieldValues>,
        userId,
        'update'
      );
    }
  },

  async delete(id: string, userId?: string): Promise<void> {
    const docRef = doc(db, COLLECTION, id);
    
    // Get current data to track deletion
    let currentData = null;
    if (userId) {
      const currentDocSnapshot = await getDoc(docRef);
      if (currentDocSnapshot.exists()) {
        currentData = { 
          id,
          ...currentDocSnapshot.data()
        } as CustomerWithFieldValues;
      }
    }
    
    // Delete the customer
    await deleteDoc(docRef);
    
    // Record history entry for deletion if we have a userId and data
    if (userId && currentData) {
      await customerHistoryService.recordChange(
        id,
        currentData,
        null,
        userId,
        'delete'
      );
    }
  },

  async getAll(): Promise<Customer[]> {
    const q = query(
      collection(db, COLLECTION),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt.toDate(),
      updatedAt: doc.data().updatedAt.toDate()
    })) as Customer[];
  },

  async getById(id: string): Promise<Customer | null> {
    const docRef = doc(db, COLLECTION, id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    return {
      id: docSnap.id,
      ...docSnap.data(),
      createdAt: docSnap.data().createdAt.toDate(),
      updatedAt: docSnap.data().updatedAt.toDate()
    } as Customer;
  },

  async getByCreator(userId: string): Promise<Customer[]> {
    const q = query(
      collection(db, COLLECTION),
      where('createdBy', '==', userId),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt.toDate(),
      updatedAt: doc.data().updatedAt.toDate()
    })) as Customer[];
  },

  async bulkDelete(ids: string[], userId?: string): Promise<{ success: number; failed: number; errors: string[] }> {
    console.log(`========== BULK DELETE START (${ids.length} customers) ==========`);
    
    const results = {
      success: 0,
      failed: 0,
      errors: [] as string[]
    };

    for (const id of ids) {
      try {
        await this.delete(id, userId);
        results.success++;
        console.log(`✓ Deleted customer: ${id}`);
      } catch (error) {
        results.failed++;
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        results.errors.push(`${id}: ${errorMessage}`);
        console.error(`✗ Failed to delete customer ${id}:`, error);
      }
    }

    console.log(`========== BULK DELETE END ==========`);
    console.log(`Success: ${results.success}, Failed: ${results.failed}`);
    
    return results;
  }
}; 